import { useEffect, useRef } from 'react'

import { useNavigate } from '@tanstack/react-router'

import {
  ResponsiveDialog,
  ResponsiveDialogContent,
} from '@/components/common/responsive-dialog'
import { PlanIconHeader, UpsellDialogShell } from '@/components/common/upsell'
import { Button } from '@/components/sui/button'
import { useComponentTranslation } from '@/hooks/use-component-translation'
import type { PlanType } from '@/lib/plans'
import { PLAN_DISPLAY_NAMES, PLAN_VISUAL_CONFIG, PLANS } from '@/lib/plans'
import { cn } from '@/lib/utils'
import { useContextStore } from '@/stores/context'

import {
  trackFeaturePaywallClosed,
  trackFeaturePaywallConverted,
  trackFeaturePaywallOpened,
} from './feature-paywall-dialog.tracking'
import locales from './locales'

interface FeaturePaywallDialogProps {
  feature: string
  minimumPlan: PlanType | null
  open: boolean
  onOpenChange: (open: boolean) => void
  source?: string | null
}

interface FeaturePaywallDialogContentProps extends FeaturePaywallDialogProps {
  featureName: string
  featureDescription: string
  benefits: Array<string>
  t: (key: string, options?: Record<string, unknown>) => string
  onUpgradeClick?: () => void
}

function PlanAvailabilityLine({
  minimumPlan,
  t,
}: {
  minimumPlan: PlanType
  t: (key: string, options?: Record<string, unknown>) => string
}) {
  return (
    <>
      {t(minimumPlan === PLANS.MAX ? 'availableIn' : 'availableFrom', {
        plan: PLAN_DISPLAY_NAMES[minimumPlan] ?? minimumPlan,
      })}
    </>
  )
}

/**
 * FeaturePaywallDialogManaged - For users managed by an administrator
 * Shows: feature info + contact admin message + close button
 * Hides: plan info + benefits + upgrade button
 */
function FeaturePaywallDialogManaged({
  featureName,
  featureDescription,
  minimumPlan,
  t,
}: FeaturePaywallDialogContentProps) {
  return (
    <UpsellDialogShell
      icon={<PlanIconHeader plan={minimumPlan} />}
      title={featureName}
      description={featureDescription}
      contactAdmin={{
        title: t('contactAdmin'),
        description: t('contactAdminDescription'),
      }}
    />
  )
}

/**
 * FeaturePaywallDialogAdmin - For administrators who can upgrade
 * Shows: feature info + plan info + benefits + upgrade button
 * When minimumPlan is null: hides plan name, benefits, shows generic upgrade button
 */
function FeaturePaywallDialogAdmin({
  featureName,
  featureDescription,
  benefits,
  minimumPlan,
  onOpenChange,
  onUpgradeClick,
  t,
  feature,
}: FeaturePaywallDialogContentProps) {
  const navigate = useNavigate()
  const visual = minimumPlan ? PLAN_VISUAL_CONFIG[minimumPlan] : null

  const handleUpgrade = () => {
    onUpgradeClick?.()

    trackFeaturePaywallConverted({
      feature,
      minimumPlan,
      currentPlan:
        useContextStore.getState().userAccountContext?.balanceInformation
          .featureType ?? 'Free',
      userRole: 'Administrator',
    })

    onOpenChange(false)
    navigate({ to: '/settings/billing/plans' })
  }

  return (
    <UpsellDialogShell
      icon={<PlanIconHeader plan={minimumPlan} />}
      title={featureName}
      planLine={
        minimumPlan ? (
          <PlanAvailabilityLine minimumPlan={minimumPlan} t={t} />
        ) : undefined
      }
      description={featureDescription}
      benefits={
        minimumPlan ? { title: t('benefitsTitle'), items: benefits } : undefined
      }
      actions={
        minimumPlan && visual ? (
          <Button
            onClick={handleUpgrade}
            className={cn(
              'cta-shimmer relative overflow-hidden font-semibold text-white',
              visual.button,
            )}
            size="lg"
          >
            {t('upgrade', {
              plan: PLAN_DISPLAY_NAMES[minimumPlan] ?? minimumPlan,
            })}
          </Button>
        ) : (
          <Button
            onClick={handleUpgrade}
            className="cta-shimmer relative overflow-hidden font-semibold"
            size="lg"
          >
            {t('upgradeGeneric')}
          </Button>
        )
      }
    />
  )
}

/**
 * FeaturePaywallDialogUser - For non-administrator users
 * Shows: feature info + plan info + benefits + contact admin message + close button
 * When minimumPlan is null: hides plan name and benefits
 * Hides: upgrade button
 */
function FeaturePaywallDialogUser({
  featureName,
  featureDescription,
  benefits,
  minimumPlan,
  t,
}: FeaturePaywallDialogContentProps) {
  return (
    <UpsellDialogShell
      icon={<PlanIconHeader plan={minimumPlan} />}
      title={featureName}
      planLine={
        minimumPlan ? (
          <PlanAvailabilityLine minimumPlan={minimumPlan} t={t} />
        ) : undefined
      }
      description={featureDescription}
      benefits={
        minimumPlan ? { title: t('benefitsTitle'), items: benefits } : undefined
      }
      contactAdmin={{
        title: t('contactAdmin'),
        description: t('contactAdminDescription'),
      }}
    />
  )
}

export function FeaturePaywallDialog({
  feature,
  minimumPlan,
  open,
  onOpenChange,
  source,
}: FeaturePaywallDialogProps) {
  const { t } = useComponentTranslation(locales)
  const hasTrackedOpen = useRef(false)
  const hasConverted = useRef(false)
  const openedAtRef = useRef<number | null>(null)

  const isManagedBy = useContextStore(
    (state) => state.userAccountContext?.accountInformation.isManaged,
  )

  const isAdministrator = useContextStore(
    (state) =>
      state.userAccountContext?.userAccountInformation.role === 'Administrator',
  )

  const currentPlan = useContextStore(
    (state) =>
      state.userAccountContext?.balanceInformation.featureType ?? 'Free',
  )

  const userRole = isAdministrator ? 'Administrator' : 'User'

  const featureName = t(`features.${feature}.name`)
  const featureDescription = t(`features.${feature}.description`)
  const benefits = minimumPlan
    ? (t(`planBenefits.${minimumPlan}`, {
        returnObjects: true,
      }) as Array<string>)
    : []

  useEffect(() => {
    if (open && !hasTrackedOpen.current) {
      trackFeaturePaywallOpened({
        feature,
        minimumPlan,
        currentPlan,
        userRole,
        isManaged: isManagedBy ?? false,
        source: source ?? null,
      })
      hasTrackedOpen.current = true
      openedAtRef.current = Date.now()
    }

    if (!open) {
      hasTrackedOpen.current = false
    }
  }, [open, feature, minimumPlan, currentPlan, userRole, isManagedBy, source])

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen && open) {
      const durationMs = openedAtRef.current
        ? Date.now() - openedAtRef.current
        : 0

      trackFeaturePaywallClosed({
        feature,
        minimumPlan,
        currentPlan,
        converted: hasConverted.current,
        durationMs,
      })

      hasConverted.current = false
      openedAtRef.current = null
    }
    onOpenChange(newOpen)
  }

  const handleUpgradeClick = () => {
    hasConverted.current = true
  }

  const contentProps: FeaturePaywallDialogContentProps = {
    feature,
    minimumPlan,
    open,
    onOpenChange: handleOpenChange,
    featureName,
    featureDescription,
    benefits,
    t,
    source,
  }

  const renderContent = () => {
    if (isManagedBy) {
      return <FeaturePaywallDialogManaged {...contentProps} />
    }

    if (isAdministrator) {
      return (
        <FeaturePaywallDialogAdmin
          {...contentProps}
          onUpgradeClick={handleUpgradeClick}
        />
      )
    }

    return <FeaturePaywallDialogUser {...contentProps} />
  }

  return (
    <ResponsiveDialog open={open} onOpenChange={handleOpenChange}>
      <ResponsiveDialogContent size="xl">
        {renderContent()}
      </ResponsiveDialogContent>
    </ResponsiveDialog>
  )
}
